import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Globe2, Stamp, ScrollText, BadgeJapaneseYen } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Reveal } from '../../components/landing/Reveal';

/**
 * About — the founder note plus the wedge that makes PlyEZ different:
 * JP-deep AND global-deep on one ledger. Washi-paper editorial, calm.
 */
const VALUES = ['honest', 'depth', 'calm', 'global'] as const;

export function AboutPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        {/* Intro */}
        <section className="washi border-b border-border">
          <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
            <Reveal as="div">
              <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
                <span className="sparkle-dot" aria-hidden />
                {t('about.eyebrow')}
              </span>
              <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-5">
                {t('about.title')}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                {t('about.lead')}
              </p>
            </Reveal>
          </div>
        </section>

        {/* The note */}
        <section className="border-b border-border">
          <div className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div" className="space-y-6 text-foreground/85 leading-relaxed text-[15px]">
              <p>{t('about.p1')}</p>
              <p>{t('about.p2')}</p>
              <p>{t('about.p3')}</p>
              <p>{t('about.p4')}</p>
            </Reveal>
          </div>
        </section>

        {/* The wedge — JP-deep + global-deep */}
        <section className="border-b border-border bg-card/40">
          <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div" className="max-w-2xl">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('about.wedge.eyebrow')}
              </span>
              <h2 className="display text-2xl lg:text-3xl font-semibold mt-3">
                {t('about.wedge.title')}
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                {t('about.wedge.body')}
              </p>
            </Reveal>

            <div className="mt-10 grid gap-5 md:grid-cols-2">
              <Reveal as="div" className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-200 flex items-center justify-center">
                    <Stamp size={18} aria-hidden />
                  </span>
                  <div className="font-medium">{t('about.wedge.jpTitle')}</div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {t('about.wedge.jpBody')}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-foreground/80">
                  {['jp1', 'jp2', 'jp3'].map((k) => (
                    <li key={k} className="flex gap-2">
                      <ScrollText size={15} className="mt-0.5 shrink-0 text-cinnabar-500" aria-hidden />
                      <span>{t(`about.wedge.${k}`)}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal as="div" delay={0.08} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-200 flex items-center justify-center">
                    <Globe2 size={18} aria-hidden />
                  </span>
                  <div className="font-medium">{t('about.wedge.globalTitle')}</div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {t('about.wedge.globalBody')}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-foreground/80">
                  {['g1', 'g2', 'g3'].map((k) => (
                    <li key={k} className="flex gap-2">
                      <BadgeJapaneseYen size={15} className="mt-0.5 shrink-0 text-cinnabar-500" aria-hidden />
                      <span>{t(`about.wedge.${k}`)}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="border-b border-border">
          <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div" className="max-w-2xl">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('about.values.eyebrow')}
              </span>
              <h2 className="display text-2xl lg:text-3xl font-semibold mt-3">
                {t('about.values.title')}
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-x-8 gap-y-10 sm:grid-cols-2">
              {VALUES.map((v, i) => (
                <Reveal key={v} as="div" delay={i * 0.06}>
                  <div className="num text-sm text-cinnabar-500">0{i + 1}</div>
                  <div className="rule mt-2" />
                  <h3 className="display text-lg font-semibold mt-3">
                    {t(`about.values.${v}.title`)}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {t(`about.values.${v}.body`)}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Founder note + CTA */}
        <section className="border-b border-border bg-card/40">
          <div className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
            <Reveal as="div">
              <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
                <div className="font-medium">{t('about.signoffName')}</div>
                <div className="text-sm text-muted-foreground">{t('about.signoffRole')}</div>
              </div>
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Link
                  to="/signup"
                  className="inline-flex h-12 items-center gap-2 px-7 rounded-lg bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
                >
                  {t('nav.signUp')}
                  <ArrowRight size={16} className="rtl-flip" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex h-12 items-center gap-2 px-7 rounded-lg border border-border text-foreground text-sm font-semibold hover:bg-muted transition-colors"
                >
                  {t('about.cta.contact')}
                </Link>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">{t('freeForever')}</p>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
